import { useEffect, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarClock,
  CheckCircle2,
  CreditCard,
  FolderKanban,
  LifeBuoy,
  Loader2,
  PhoneCall,
  Plus,
  ReceiptText,
  Target,
  UserPlus,
} from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { getDashboard, type DashPeriod, type DashboardPayload } from "../../../lib/dashboard";
import { goalPct } from "../../../lib/goals";
import {
  Btn,
  Card,
  Kpi,
  PageHead,
  PillTabs,
  fmtDuration,
  fmtMoney,
  fmtNum,
  fmtPct,
  useCountUp,
  useToast,
  type KpiGoal,
} from "../../ui";
import { NewClientModal } from "../_shared/NewClientModal";
import { AgendaCard } from "./panels/AgendaCard";
import { GoalsModal } from "./panels/GoalsModal";
import { ActivityCard } from "./panels/ActivityCard";
import { AttentionCard } from "./panels/AttentionCard";
import { CampaignsCard } from "./panels/CampaignsCard";
import { FeedCard } from "./panels/FeedCard";
import { FunnelCard } from "./panels/FunnelCard";
import { InvoicesCard } from "./panels/InvoicesCard";
import { RevenueCard } from "./panels/RevenueCard";
import { SourcesCard } from "./panels/SourcesCard";

const PERIODS: DashPeriod[] = ["month", "quarter", "year"];

/** A KPI figure that rolls up on first paint, formatted on the way. */
function Rolling({ value, format }: { value: number; format: (n: number) => string }) {
  return <>{format(useCountUp(value))}</>;
}

/** One cell of the quick strip - the numbers that need no chart to be read. */
function Quick({ icon: Icon, label, value, sub }: { icon: typeof Target; label: string; value: string; sub?: string }) {
  return (
    <div className="crm-q">
      <span className="crm-q-tile">
        <Icon className="size-4" strokeWidth={1.8} />
      </span>
      <span>
        <span className="crm-q-lab block">{label}</span>
        <span className="crm-q-val block">
          {value}
          {sub ? <span className="crm-mut ml-1 text-xs font-semibold">{sub}</span> : null}
        </span>
      </span>
    </div>
  );
}

/**
 * The operator's overview.
 *
 * Everything here is one request (see lib/dashboard.ts). The period tabs move
 * only the four headline tiles and their comparison window - the twelve-month
 * curve, the pipeline and today's agenda are either fixed-length by definition
 * or a snapshot of right now, and re-cutting them to the selected period would
 * answer a question nobody asked.
 */
export function DashboardSection({ user }: { user: StaffUser }) {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const [period, setPeriod] = useState<DashPeriod>("month");
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [failed, setFailed] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingGoals, setEditingGoals] = useState(false);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    getDashboard(period)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [period]);

  const canSell = userCanPlatform(user, "manage:crm.sales");

  if (failed) {
    return (
      <>
        <PageHead title={t("dashboard.title")} sub={t("dash.failed")} />
      </>
    );
  }

  if (!data) {
    return (
      <>
        <PageHead title={t("dashboard.title")} sub={t("dashboard.intro")} />
        <div className="flex justify-center py-24 text-muted-foreground">
          <Loader2 className="size-7 animate-spin" />
        </div>
      </>
    );
  }

  const { kpi, quick } = data;

  /**
   * A tile's target, or null when the desk has not set one for this window.
   *
   * Only the three tiles that measure the WINDOW can carry one. The fourth
   * (outstanding invoices) is a balance at this instant rather than a total
   * accumulated over the period, so "8 000 of 10 000" there would read as
   * progress towards being owed more money - see `CrmGoalSet` on the server.
   */
  const goalFor = (current: number, target: number | null, format: (n: number) => string): KpiGoal | null => {
    const pct = goalPct(current, target);
    if (pct === null || target === null) return null;
    return { pct, foot: t("dash.goal.of", { done: format(current), target: format(target) }) };
  };

  // Two facts under one number, and both are needed to read it: the headline
  // counts dials, so without the connected count a busy morning of nobody
  // picking up looks exactly like a busy morning of conversations. The talk
  // time comes last because it is the one an idle day can leave out.
  const callsSub = [
    quick.callsToday ? t("dash.quick.connected", { n: quick.connectedToday }) : "",
    quick.callSecondsToday ? fmtDuration(quick.callSecondsToday) : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <PageHead
        title={t("dashboard.title")}
        sub={t("dash.intro")}
        actions={
          <>
            <PillTabs
              value={period}
              onChange={setPeriod}
              tabs={PERIODS.map((p) => ({ value: p, label: t(`dash.period.${p}`) }))}
            />
            {/* Beside the period tabs on purpose: the tabs choose which window
                is on screen, and this sets what that same window is FOR. */}
            {canSell ? (
              <Btn onClick={() => setEditingGoals(true)}>
                <Target className="size-4" />
                {t("dash.goal.edit")}
              </Btn>
            ) : null}
            {userCanPlatform(user, "manage:crm.clients") ? (
              <Btn tone="brand" onClick={() => setAdding(true)}>
                <Plus className="size-4" />
                {t("clients.add")}
              </Btn>
            ) : null}
          </>
        }
      />

      {/* Everything below sizes itself against this box rather than the window,
          so collapsing the rail really does buy another column. The wrapper stays
          tight around the grid rows: `container-type` brings layout containment
          with it, which would make this element the containing block for any
          `position: fixed` descendant. */}
      <div className="crm-dash">
        <div className="crm-kpis">
          <Kpi
            icon={ReceiptText}
            tone="primary"
            label={t("dash.kpi.revenue")}
            value={<Rolling value={kpi.revenueCents} format={(n) => fmtMoney(n)} />}
            delta={kpi.revenueDeltaPct}
            foot={t(`dash.vsPrev.${period}`)}
            goal={goalFor(kpi.revenueCents, data.goals.revenueCents, (n) => fmtMoney(n))}
            series={data.series.map((m) => m.collectedCents)}
          />
          <Kpi
            icon={UserPlus}
            tone="success"
            label={t("dash.kpi.leads")}
            value={<Rolling value={kpi.leadsNew} format={fmtNum} />}
            delta={kpi.leadsNewDeltaPct}
            foot={t(`dash.vsPrev.${period}`)}
            goal={goalFor(kpi.leadsNew, data.goals.leadsNew, fmtNum)}
          />
          <Kpi
            icon={CheckCircle2}
            tone="primary2"
            label={t("dash.kpi.won")}
            value={<Rolling value={kpi.dealsWon} format={fmtNum} />}
            delta={kpi.dealsWonDeltaPct}
            foot={t(`dash.vsPrev.${period}`)}
            goal={goalFor(kpi.dealsWon, data.goals.dealsWon, fmtNum)}
          />
          <Kpi
            icon={CreditCard}
            tone="destructive"
            label={t("dash.kpi.outstanding")}
            value={<Rolling value={kpi.outstandingCents} format={(n) => fmtMoney(n)} />}
            foot={t("dash.kpi.overdueFoot", { n: kpi.outstandingCount, late: kpi.overdueCount })}
            series={data.series.map((m) => m.invoicedCents)}
          />
        </div>

        {/* Five cells now, so the column count has to be said out loud: the
            default is four, and a fifth would have wrapped onto a row of its
            own. A variable rather than an inline template, because an inline
            one cannot be overridden and the strip has to fold on a phone - see
            `.crm-quick` in index.css. */}
        <Card className="crm-quick" style={{ "--crm-quick-cols": 5, "--crm-quick-cols-xs": 2 } as CSSProperties}>
          <Quick
            icon={LifeBuoy}
            label={t("dash.quick.tickets")}
            value={fmtNum(quick.openTickets)}
            sub={quick.urgentTickets ? t("dash.quick.urgent", { n: quick.urgentTickets }) : undefined}
          />
          <Quick
            icon={PhoneCall}
            label={t("dash.quick.calls")}
            value={fmtNum(quick.callsToday)}
            sub={callsSub || undefined}
          />
          <Quick icon={Target} label={t("dash.quick.conversion")} value={fmtPct(quick.conversionPct)} />
          <Quick icon={FolderKanban} label={t("dash.quick.projects")} value={fmtNum(quick.activeProjects)} />
          {/* The one figure on this strip that is a gap rather than a volume:
              every other cell counts work that exists, this one counts work
              nobody has scheduled. It reads as a number because a zero here is
              the good answer and deserves to be visible. */}
          <Quick
            icon={CalendarClock}
            label={t("dash.quick.noNextStep")}
            value={fmtNum(quick.leadsNoNextStep)}
          />
        </Card>

        <div className="crm-cols">
          <RevenueCard series={data.series} />
          <FunnelCard funnel={data.funnel} />
        </div>

        <div className="crm-cols-ab">
          <CampaignsCard campaigns={data.campaigns} />
          <SourcesCard sources={data.sources} />
        </div>

        <div className="crm-cols3">
          <AgendaCard agenda={data.agenda} />
          <ActivityCard activity={data.activity} />
          <AttentionCard attention={data.attention} />
        </div>

        {/* Without the sales permission the invoice panel is gone and the feed is
            the only card left, so the row drops to one column instead of leaving
            the second one empty. */}
        <div className={cn("crm-cols-ab", !canSell && "crm-cols-solo")}>
          {canSell ? <InvoicesCard invoices={data.invoices} /> : null}
          <FeedCard feed={data.feed} />
        </div>
      </div>

      {adding ? (
        <NewClientModal onClose={() => setAdding(false)} onCreate={(c) => navigate(`/clients/${c.id}`)} />
      ) : null}

      {editingGoals ? (
        <GoalsModal
          period={period}
          current={data.goals}
          onClose={() => setEditingGoals(false)}
          onSaved={(next) => {
            // Patched in rather than refetched: nothing else on this screen can
            // have changed, and a full reload would blank every tile and replay
            // the count-up animation for a number that did not move.
            setData((d) => (d ? { ...d, goals: next } : d));
            setEditingGoals(false);
            toast(t("common.saved"));
          }}
          onError={() => toast(t("common.error"))}
        />
      ) : null}
    </>
  );
}
